import {Injectable, Inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, take} from "rxjs";
import {VisitPlanJobActions, VisitPlanJobAPiActions} from "../state/visit-plan-job-state/visit-plan-job.actions";
import {visitPlanJobFeature} from "../state/visit-plan-job-state/visit-plan-job.state";
import {VisitPlanJobModel} from "../../models/visit-plan-job.model";
import {VisitScheduleResourcesParamsModel} from "../../models/visit-schedule-params.model";
import {VisitScheduleResourceModel} from "../../models/visit-schedule-resource.model";
import {VisitPlanHttpService} from "./visit-plan-http.service";
import {VisitScheduleOptimizationSettings} from "../../models/visit-schedule-optimization-config.model";
import {VisitScheduleAnalysisModel} from "../../models/visit-schedule-analysis.model";
import {VisitScheduleRouteOptimizeRequestModel} from "../../models/visit-schedule-route-optimize-request.model";

@Injectable()
export class VisitPlanScheduleFacadeService {

  constructor(@Inject(Store) private store: Store,
              @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService) {
  }

  fetchNextJobs(visitPlanId: string, visitPlanScheduleId, params?: Object): void {
    this.store.select(visitPlanJobFeature.selectCursor()).pipe(take(1)).subscribe((cursor) => {
      if (!!cursor) {
        this.store.dispatch(VisitPlanJobAPiActions.getVisitPlanJobs({visitPlanId, visitPlanScheduleId, params}));
      }
    })
  }

  getManyJobs(visitPlanId: string, visitPlanScheduleId?: string, params?: Object): void {
    this.store.dispatch(VisitPlanJobAPiActions.getVisitPlanJobs({visitPlanId, visitPlanScheduleId, params}));
  }

  selectAllJobs(): Observable<VisitPlanJobModel[]> {
    return this.store.select(visitPlanJobFeature.selectVisitPlanJobs());
  }

  selectIsLoadingJobs(): Observable<boolean> {
    return this.store.select(visitPlanJobFeature.selectIsLoading());
  }

  clearStore(): void {
    this.store.dispatch(VisitPlanJobActions.clearStore());
  }

  getResources(visitPlanId: string, visitScheduleId: string, query: VisitScheduleResourcesParamsModel): Observable<{
    list: VisitScheduleResourceModel[],
    cursor?: string
  }> {
    return this.visitPlanHttpService.getResources(visitPlanId, visitScheduleId, query);
  }

  getAssignOptions(visitPlanId: string, visitScheduleId: string, jobIds?: string[], callIds?: string[], requestedRouteId?: string, params?: any): Observable<any> {
    return this.visitPlanHttpService.getAssignOptions(visitPlanId, visitScheduleId, jobIds, callIds, requestedRouteId, params);
  }

  createVisitScheduleRoute(visitPlanId: string, visitScheduleId: string, payload: {
    resourceId: string,
    jobIds: string[]
  }): Observable<any> {
    return this.visitPlanHttpService.createVisitScheduleRoute(visitPlanId, visitScheduleId, payload);
  }

  getVisitScheduleRoutesPagination(visitPlanId: string, visitScheduleId: string, params?: any): Observable<any> {
    return this.visitPlanHttpService.getVisitScheduleRoutesPagination(visitPlanId, visitScheduleId, params);
  }

  getVisitScheduleRoutesCursor(visitPlanId: string, visitScheduleId: string, params?: any): Observable<any> {
    return this.visitPlanHttpService.getVisitScheduleRoutesCursor(visitPlanId, visitScheduleId, params);
  }

  getVisitScheduleAgentsUtilization(visitPlanId: string, visitScheduleId: string, params?: any): Observable<any> {
    return this.visitPlanHttpService.getVisitScheduleAgentsUtilization(visitPlanId, visitScheduleId, params);
  }

  getVisitScheduleRisks(visitPlanId: string, visitScheduleId: string, params?: any): Observable<VisitScheduleAnalysisModel> {
    return this.visitPlanHttpService.getVisitScheduleRisks(visitPlanId, visitScheduleId, params);
  }

  visitScheduleRouteOptimize(visitPlanId: string, visitScheduleId: string, visitScheduleRouteOptimizePayload: VisitScheduleRouteOptimizeRequestModel): Observable<any> {
    return this.visitPlanHttpService.visitScheduleRouteOptimize(visitPlanId, visitScheduleId, visitScheduleRouteOptimizePayload);
  }

  publishVisitSchedule(visitPlanId: string, visitScheduleId: string, params?: { routeIds?: string[], visitScheduleDayIds?: string[] }): Observable<any> {
    return this.visitPlanHttpService.publishVisitSchedule(visitPlanId, visitScheduleId, params);
  }

  deleteRoutes(visitPlanId: string, visitScheduleId: string, routeIds: string[]): Observable<any> {
    return this.visitPlanHttpService.deleteRoutes(visitPlanId, visitScheduleId, routeIds);
  }

  optimizeVisitSchedule(visitPlanId: string, visitScheduleId: string, visitScheduleOptimizationSettings: VisitScheduleOptimizationSettings, dayIds: string[]): Observable<any> {
    return this.visitPlanHttpService.optimizeVisitSchedule(visitPlanId, visitScheduleId, visitScheduleOptimizationSettings, dayIds);
  }

  moveRoute(visitPlanId: string, visitScheduleId: string, routeId: string, resourceId: string, visitScheduleDayId: string): Observable<any[]> {
    return this.visitPlanHttpService.moveRoute(visitPlanId, visitScheduleId, routeId, resourceId, visitScheduleDayId);
  }

}
